/**
 * Workspace entry for a signed-in IDC person without a Software Tracker role (AIC-1486, plan D15). With the Notetaker
 * flag on, App.tsx asks workspaceEntry before calling tracker-api: a token with a Software Tracker role boots exactly
 * as before (/api/portal/me), and one without boots here, with no tracker-api request at all (no /me, no counts poll,
 * no onboarding, no bell, no telemetry, no warm-up). With the flag off this never runs, so the portal is as before
 * ("Your account needs access").
 *
 * The token is read for routing and display only; tracker-api and notetaker-api check every request themselves.
 */
import { useEffect, useLayoutEffect } from "react";
import type { Auth } from "../auth/session.ts";
import { DeferredView } from "../components/DeferredView.tsx";
import { Frame, Shell, Standalone } from "../components/Shell.tsx";
import { hasPortalRole } from "../roles.ts";
import { navigate } from "../router.tsx";
import { notetakerEnabled } from "./flag.ts";
import type { NotetakerShell } from "./shell.tsx";
import { tokenClaims } from "./roles.ts";

export const WORKSPACE_NAME = "IDC Analyst Workspace";

export interface WorkspacePerson { name: string; email: string }

/**
 * The person to boot into the Workspace, or null for today's boot (/me): the flag is off, there is no token, it can't
 * be read, or it carries a Software Tracker role. A failed token read is null too, so /me reports it as it always has.
 */
export async function workspaceEntry(auth: Pick<Auth, "getToken">): Promise<WorkspacePerson | null> {
  if (!notetakerEnabled()) return null;
  let token: string | null;
  try { token = await auth.getToken(); } catch { return null; }
  const claims = tokenClaims(token);
  if (!claims || hasPortalRole(claims.roles)) return null;
  return { name: claims.name, email: claims.email };
}

function Redirect({ to }: { to: string }) {
  useLayoutEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}

/** Nothing in the Workspace is open to this person yet: plain words, never the Software Tracker's "needs access". */
function NothingHere({ person, onSignOut }: { person: WorkspacePerson; onSignOut: () => void }) {
  useEffect(() => { document.title = WORKSPACE_NAME; }, []);
  return (
    <Frame mode="build" skipTo="main-ws" onSignOut={onSignOut} person={person} appName={WORKSPACE_NAME} workspaceOnly>
      <main className="ipd-public" id="main-ws" tabIndex={-1}>
        <Standalone title={WORKSPACE_NAME}><p>Nothing here is available to your account yet.</p></Standalone>
      </main>
    </Frame>
  );
}

/**
 * The Workspace for a person without a Software Tracker role. Notetaker, when it is shown to them, is the whole
 * workspace: any other address goes to its landing page, so no Software Tracker screen (and no tracker-api read) runs.
 * No mode band, and no Home, Help or My profile links: those are Software Tracker's (Bill, 2026-10-22). The bar keeps
 * BUILD, the portal's safe default before tracker-api says otherwise.
 */
export function WorkspaceOnly({ person, path, notetaker, onSignOut }: { person: WorkspacePerson; path: string; notetaker: NotetakerShell; onSignOut: () => void }) {
  if (notetaker.view) {
    return (
      <Shell path={path} name={person.name} email={person.email} roles={[]} buildMode onSignOut={onSignOut} module={notetaker.module} entry={notetaker.entry} workspaceOnly>
        <DeferredView resetKey={path}>{notetaker.view}</DeferredView>
      </Shell>
    );
  }
  if (!notetaker.ready) return <Frame mode="build" skipTo="main-ws" appName={WORKSPACE_NAME} workspaceOnly><main className="ipd-public" id="main-ws" tabIndex={-1}><p role="status">Loading…</p></main></Frame>;
  if (notetaker.entry) return <Redirect to={notetaker.entry.to} />;
  return <NothingHere person={person} onSignOut={onSignOut} />;
}
